import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BadgePercent, Eye, EyeOff, Search, Trash2, CalendarClock, Loader2, Star } from "lucide-react";

export default function Offers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [offerFor, setOfferFor] = useState(null);
  const [oType, setOType] = useState("percent");
  const [oValue, setOValue] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/medicines").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const expirySoon = useMemo(() => {
    const limit = Date.now() + 60 * 86400000;
    return items.filter((m) => m.is_active !== false && !(m.offer_value > 0) && m.expiry_date && new Date(m.expiry_date).getTime() < limit);
  }, [items]);

  const filtered = items.filter((m) => !q || (m.name || "").toLowerCase().includes(q.toLowerCase()));

  const saveOffer = async () => {
    setBusy("offer");
    try {
      await api.put(`/medicines/${offerFor.id}/flags`, { offer_type: oType, offer_value: Number(oValue) || 0 });
      toast.success(`Offer lag gaya — website par turant dikhega`);
      setOfferFor(null);
      setOValue("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Offer save nahi hua");
    } finally {
      setBusy("");
    }
  };

  const clearOffer = async (m) => {
    setBusy(m.id);
    try {
      await api.put(`/medicines/${m.id}/flags`, { offer_type: "", offer_value: 0 });
      toast.success("Offer hata diya");
      load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy("");
    }
  };

  const toggleActive = async (m) => {
    setBusy(m.id);
    try {
      await api.put(`/medicines/${m.id}/flags`, { is_active: m.is_active === false });
      toast.success(m.is_active === false ? `${m.name} website par WAPAS dikhega` : `${m.name} website se HIDE ho gaya`);
      load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy("");
    }
  };

  const offerLabel = (m) => (m.offer_type === "percent" ? `${m.offer_value}% OFF` : m.offer_type === "fixed" ? `Flat ₹${m.offer_value}` : "");

  return (
    <div className="space-y-5" data-testid="offers-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Offers &amp; Website Visibility</h1>
        <p className="text-sm text-slate-500">Jis medicine par offer lagana hai select karke lagao — website ke Offers page par turant dikhega. Hide karne par medicine website se gayab ho jayegi.</p>
      </div>

      {expirySoon.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="expiry-suggest-card">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-bold text-amber-800">Expiry kareeb (60 din) — offer laga kar jaldi becho:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {expirySoon.map((m) => (
              <button key={m.id} onClick={() => { setOfferFor(m); setOType("percent"); setOValue(""); }} data-testid={`expiry-suggest-${m.id}`}
                className="text-xs bg-white border border-amber-300 text-amber-800 rounded-full px-3 py-1.5 font-semibold hover:bg-amber-100">
                {m.name} • Exp {m.expiry_date}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4 border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="offers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Medicine search karein…" className="pl-9" />
        </div>
      </Card>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Medicine</TableHead>
                <TableHead className="text-right">MRP</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Offer</TableHead>
                <TableHead>Website</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m) => (
                <TableRow key={m.id} data-testid={`offer-row-${m.id}`} className={m.is_active === false ? "opacity-50" : ""}>
                  <TableCell>
                    <div className="text-sm font-semibold">{m.name}</div>
                    <div className="text-xs text-slate-400">{m.generic_name || m.manufacturer}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono-num">₹{Number(m.strip_mrp || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{m.expiry_date || "—"}</TableCell>
                  <TableCell data-testid={`offer-badge-${m.id}`}>
                    {m.offer_value > 0 ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{offerLabel(m)}</Badge> : <span className="text-xs text-slate-300">—</span>}
                  </TableCell>
                  <TableCell data-testid={`visibility-${m.id}`}>
                    {m.is_active === false ? (
                      <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Hidden</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Live</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {busy === m.id ? <Loader2 className="w-4 h-4 animate-spin my-2" /> : (
                        <>
                          <Button size="sm" variant="outline" className="h-8" data-testid={`set-offer-${m.id}`} onClick={() => { setOfferFor(m); setOType(m.offer_type || "percent"); setOValue(m.offer_value || ""); }}>
                            <BadgePercent className="w-3.5 h-3.5 mr-1" /> Offer
                          </Button>
                          {m.offer_value > 0 && (
                            <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" data-testid={`clear-offer-${m.id}`} onClick={() => clearOffer(m)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className={`h-8 ${m.is_featured ? "text-amber-600 border-amber-300 bg-amber-50" : ""}`} data-testid={`toggle-featured-${m.id}`} title="Homepage par Featured dikhao" onClick={async () => { setBusy(m.id); try { await api.put(`/medicines/${m.id}/flags`, { is_featured: !m.is_featured }); toast.success(m.is_featured ? `${m.name} featured se hata diya` : `${m.name} ab homepage par Featured dikhega`); load(); } catch { toast.error("Failed"); } finally { setBusy(""); } }}>
                            <Star className={`w-3.5 h-3.5 ${m.is_featured ? "fill-amber-400 text-amber-400" : ""}`} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8" data-testid={`toggle-visibility-${m.id}`} onClick={() => toggleActive(m)}>
                            {m.is_active === false ? <><Eye className="w-3.5 h-3.5 mr-1" /> Show</> : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</>}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Koi medicine nahi mili</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!offerFor} onOpenChange={(o) => !o && setOfferFor(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-display">Offer — {offerFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">MRP: <span className="font-mono-num font-bold">₹{Number(offerFor?.strip_mrp || 0).toFixed(2)}</span></div>
            <div>
              <Label className="text-xs">Offer type</Label>
              <Select value={oType} onValueChange={setOType}>
                <SelectTrigger className="mt-1 h-10 bg-white" data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="percent">% Discount (e.g. 20 = 20% off)</SelectItem>
                  <SelectItem value="fixed">Fixed Price (e.g. 45 = ₹45 mein becho)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{oType === "percent" ? "Discount %" : "Offer price ₹"}</Label>
              <Input type="number" min="0" value={oValue} onChange={(e) => setOValue(e.target.value)} className="mt-1 h-10 font-mono-num" data-testid="offer-value-input" />
            </div>
            {oValue > 0 && (
              <div className="text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="offer-preview">
                Website par dikhega: <span className="line-through text-slate-400">₹{Number(offerFor?.strip_mrp || 0).toFixed(2)}</span>{" "}
                <span className="font-mono-num font-bold text-emerald-800">₹{oType === "percent" ? (offerFor.strip_mrp * (1 - Number(oValue) / 100)).toFixed(2) : Number(oValue).toFixed(2)}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferFor(null)}>Cancel</Button>
            <Button onClick={saveOffer} disabled={!(Number(oValue) > 0) || busy === "offer"} data-testid="offer-save-btn" className="bg-emerald-900 hover:bg-emerald-800">
              {busy === "offer" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Offer Lagao"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
